import { useState } from "react";
import {
  isSameDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameMonth,
  format,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plane, LogOut, Clock, User, Phone, MapPin, Luggage, CreditCard, Car } from "lucide-react";

interface BookingsCalendarProps {
  rows: any[];
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
  cancelled: "bg-red-500/15 text-red-700 border-red-200",
  completed: "bg-green-500/15 text-green-700 border-green-200",
  in_progress: "bg-purple-500/15 text-purple-700 border-purple-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  completed: "Completed",
  in_progress: "In Progress",
};

const pickupStatusColors: Record<string, string> = {
  "No Confirmado": "bg-yellow-500/15 text-yellow-700",
  "Confirmado": "bg-green-500/15 text-green-700",
  "Recogido": "bg-blue-500/15 text-blue-700",
  "No Show": "bg-red-500/15 text-red-700",
};

const paymentMethodLabels: Record<string, string> = {
  cash: "Cash",
  paypal: "PayPal",
  stripe: "Card",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getCalendarDays(month: Date) {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  return eachDayOfInterval({ start, end });
}

function isSameUTCDay(isoStr: string, cellDay: Date) {
  const d = new Date(isoStr);
  return (
    d.getUTCFullYear() === cellDay.getFullYear() &&
    d.getUTCMonth() === cellDay.getMonth() &&
    d.getUTCDate() === cellDay.getDate()
  );
}

function getBookingsForDay(day: Date, bookings: any[]) {
  const arrivals = bookings.filter((b) => isSameUTCDay(b.pickup_date, day));
  const departures = bookings.filter((b) => b.return_date && isSameUTCDay(b.return_date, day));
  return { arrivals, departures };
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    return formatInTimeZone(new Date(dateStr), "UTC", "h:mm a");
  } catch {
    return "";
  }
}

function DetailRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  if (!value || value === "—") return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div className="min-w-0">
        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

export function BookingsCalendar({ rows }: BookingsCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);

  const filtered = rows;
  const days = getCalendarDays(currentMonth);
  const selectedDayData = selectedDay ? getBookingsForDay(selectedDay, filtered) : null;

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">{format(currentMonth, "MMMM yyyy")}</h3>
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar grid */}
      <Card>
        <CardContent className="p-2 sm:p-4">
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">
                {d}
              </div>
            ))}

            {days.map((day) => {
              const inMonth = isSameMonth(day, currentMonth);
              const today = isToday(day);
              const { arrivals, departures } = getBookingsForDay(day, filtered);
              const hasArrivals = arrivals.length > 0;
              const hasDepartures = departures.length > 0;
              const hasEvents = hasArrivals || hasDepartures;

              let cellBg = "";
              if (hasArrivals && hasDepartures) cellBg = "bg-purple-100 dark:bg-purple-900/30";
              else if (hasArrivals) cellBg = "bg-purple-50 dark:bg-purple-900/20";
              else if (hasDepartures) cellBg = "bg-green-50 dark:bg-green-900/20";

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => hasEvents && setSelectedDay(day)}
                  disabled={!hasEvents}
                  className={`
                    relative min-h-[70px] sm:min-h-[85px] rounded-md border p-1 text-left transition-all
                    ${inMonth ? "" : "opacity-30"}
                    ${today ? "ring-2 ring-primary" : ""}
                    ${cellBg}
                    ${hasEvents ? "cursor-pointer hover:ring-2 hover:ring-primary/50" : "cursor-default"}
                  `}
                >
                  <span className={`text-xs font-medium ${today ? "text-primary font-bold" : "text-foreground"}`}>
                    {format(day, "d")}
                  </span>
                  {hasArrivals && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <Plane className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                      <span className="text-[10px] font-semibold text-purple-700 dark:text-purple-300">
                        {arrivals.length}
                      </span>
                    </div>
                  )}
                  {hasDepartures && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <LogOut className="h-3 w-3 text-green-600 dark:text-green-400" />
                      <span className="text-[10px] font-semibold text-green-700 dark:text-green-300">
                        {departures.length}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-purple-400" />
          <span>Arrivals (Pickup)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-sm bg-green-400" />
          <span>Departures (Return)</span>
        </div>
      </div>

      {/* Day detail dialog */}
      <Dialog open={!!selectedDay && !selectedBooking} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDay && format(selectedDay, "EEEE, MMMM d, yyyy")}</DialogTitle>
          </DialogHeader>

          {selectedDayData && (
            <div className="space-y-4">
              {selectedDayData.arrivals.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-1">
                    <Plane className="h-4 w-4" /> Arrivals ({selectedDayData.arrivals.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.arrivals.map((b: any) => (
                      <BookingCard key={b.id} booking={b} type="arrival" onClick={() => setSelectedBooking(b)} />
                    ))}
                  </div>
                </div>
              )}
              {selectedDayData.departures.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-2 flex items-center gap-1">
                    <LogOut className="h-4 w-4" /> Departures ({selectedDayData.departures.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.departures.map((b: any) => (
                      <BookingCard key={b.id} booking={b} type="departure" onClick={() => setSelectedBooking(b)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking detail dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && setSelectedBooking(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-primary">{selectedBooking?.booking_code || "—"}</span>
              {selectedBooking && (
                <Badge variant="outline" className={statusColors[selectedBooking.status] || ""}>
                  {statusLabels[selectedBooking.status] || selectedBooking.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-1">
              <DetailRow label="Customer" value={selectedBooking.customer_name} icon={<User className="h-3.5 w-3.5" />} />
              <DetailRow label="Phone" value={selectedBooking.customer_phone} icon={<Phone className="h-3.5 w-3.5" />} />
              <DetailRow label="Email" value={selectedBooking.customer_email} />
              <DetailRow label="Pickup" value={selectedBooking.pickup_location} icon={<MapPin className="h-3.5 w-3.5" />} />
              <DetailRow label="Dropoff" value={selectedBooking.dropoff_location} icon={<MapPin className="h-3.5 w-3.5" />} />
              <DetailRow
                label="Pickup Date"
                value={formatInTimeZone(new Date(selectedBooking.pickup_date), "UTC", "PPP 'at' p")}
                icon={<Clock className="h-3.5 w-3.5" />}
              />
              {selectedBooking.return_date && (
                <DetailRow
                  label="Return Date"
                  value={formatInTimeZone(new Date(selectedBooking.return_date), "UTC", "PPP 'at' p")}
                  icon={<Clock className="h-3.5 w-3.5" />}
                />
              )}
              <DetailRow label="Flight" value={selectedBooking.flight_number} icon={<Plane className="h-3.5 w-3.5" />} />
              <DetailRow label="Airline" value={selectedBooking.airline} />
              {selectedBooking.flight_number_return && (
                <DetailRow label="Return Flight" value={selectedBooking.flight_number_return} />
              )}
              {selectedBooking.airline_return && (
                <DetailRow label="Return Airline" value={selectedBooking.airline_return} />
              )}
              <DetailRow label="Passengers" value={selectedBooking.passengers} />
              <DetailRow label="Luggage" value={selectedBooking.luggage_amount} icon={<Luggage className="h-3.5 w-3.5" />} />
              <DetailRow label="Vehicle" value={(selectedBooking.vehicles as any)?.name} icon={<Car className="h-3.5 w-3.5" />} />
              <DetailRow label="Service Type" value={selectedBooking.service_type} />
              <DetailRow label="Route" value={selectedBooking.route_type} />
              <DetailRow
                label="Payment"
                value={paymentMethodLabels[selectedBooking.payment_method] || selectedBooking.payment_method}
                icon={<CreditCard className="h-3.5 w-3.5" />}
              />
              <DetailRow
                label="Total"
                value={<span className="text-green-700 font-bold">${selectedBooking.total_price}</span>}
              />
              <DetailRow
                label="Pickup Status"
                value={
                  <Badge variant="outline" className={pickupStatusColors[selectedBooking.estatus_recojida] || ""}>
                    {selectedBooking.estatus_recojida || "No Confirmado"}
                  </Badge>
                }
              />
              {selectedBooking.special_requests && (
                <DetailRow label="Special Requests" value={selectedBooking.special_requests} />
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBooking(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingCard({ booking, type, onClick }: { booking: any; type: "arrival" | "departure"; onClick: () => void }) {
  const isCancelled = booking.status === "cancelled";
  const time = type === "arrival" ? formatTime(booking.pickup_date) : formatTime(booking.return_date);

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border p-3 space-y-1 text-sm cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all ${isCancelled ? "opacity-50" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-bold text-primary">{booking.booking_code || "—"}</span>
          {time && (
            <Badge variant="outline" className={type === "arrival" ? "bg-purple-500/10 text-purple-700 border-purple-200" : "bg-green-500/10 text-green-700 border-green-200"}>
              <Clock className="h-3 w-3 mr-1" />
              {time}
            </Badge>
          )}
        </div>
        <Badge variant="outline" className={statusColors[booking.status] || ""}>
          {statusLabels[booking.status] || booking.status}
        </Badge>
      </div>
      <div className={`font-medium ${isCancelled ? "line-through decoration-red-400" : ""}`}>{booking.customer_name}</div>
      <div className="text-xs text-muted-foreground">
        <span>{booking.pickup_location}</span>
        <span className="text-muted-foreground"> → </span>
        <span>{booking.dropoff_location}</span>
      </div>
      {(() => {
        const airline = type === "arrival" ? booking.airline : booking.airline_return;
        const flightNo = type === "arrival" ? booking.flight_number : booking.flight_number_return;
        if (!airline && !flightNo) return null;
        return (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Plane className="h-3 w-3" />
            <span>Vuelo: {[airline, flightNo].filter(Boolean).join(" ")}</span>
          </div>
        );
      })()}
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-muted-foreground">
          {(booking.vehicles as any)?.name
            || (booking.service_type
              ? String(booking.service_type).charAt(0).toUpperCase() + String(booking.service_type).slice(1)
              : "—")}
        </span>
        <span className="font-semibold text-green-700">${booking.total_price}</span>
      </div>
    </div>
  );
}
